import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { updateWorkOrder } from "../../../api/work-order";
import { WorkOrderAccessDialog } from "./WorkOrderAccessDialog";
import { WorkOrderParticipantStatus } from "./utils";

/**
 * Controlled participants section: parent owns `participants` and updates via
 * `onParticipantsChange` after PUT — no full work order refetch / page reload.
 */
export const WorkOrderAccessesSection = ({
  workOrderId,
  participants = [],
  onParticipantsChange,
  canManage,
  className = "",
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = async (participantId) => {
    if (!window.confirm(t("work_order.participants.delete_confirm"))) return;

    try {
      setError(null);
      const updated = (participants || []).map((p) =>
        p.id === participantId ? { ...p, delete: true } : p
      );
      await updateWorkOrder({ id: workOrderId, participants: updated });
      onParticipantsChange(
        (participants || []).filter((p) => p.id !== participantId)
      );
    } catch (err) {
      console.error(err);
      setError(t("work_order.participants.delete_error"));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSuccess = (list) => {
    if (onParticipantsChange && Array.isArray(list)) {
      onParticipantsChange(list);
    }
    handleDialogClose();
  };

  return (
    <>
      <div
        className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {t("work_order.participants.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {participants.length}{" "}
              {participants.length === 1 ? "deltaker" : "deltakere"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {participants.length > 0 && (
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                {t("work_order.participants.status")}
              </span>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" />
                {t("work_order.participants.add")}
              </button>
            )}
          </div>
        </div>
        <div className="px-5 py-4">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}
          {participants.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              <p>{t("work_order.participants.none")}</p>
              {canManage && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("work_order.participants.add_first")}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {participants.map((p) => (
                <div
                  key={p.id}
                  className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3 transition-colors hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-0 text-sm font-medium text-gray-900">
                        {p.user ? (
                          <Link
                            to={`/dashboard/users/${p.user.id}`}
                            className="truncate text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {p.displayTitle || p.user?.displayTitle || "—"}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </div>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                        {p.role || "—"}
                      </span>
                    </div>
                    <div className="mt-2">
                      <dd>
                        <WorkOrderParticipantStatus participant={p} />
                      </dd>
                    </div>
                  </div>
                  {canManage && (
                    <div className="ml-2 flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(p);
                          setDialogOpen(true);
                        }}
                        className="inline-flex items-center rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        title={t("work_order.participants.edit")}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        className="inline-flex items-center rounded-md border border-red-200 bg-white p-2 text-sm text-red-700 transition-colors hover:bg-red-50"
                        title={t("work_order.participants.delete")}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <WorkOrderAccessDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        workOrderId={workOrderId}
        access={editing}
        onSuccess={handleSuccess}
      />
    </>
  );
};
